import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Download,
  ErrorOutline,
  UploadFile,
} from "@mui/icons-material";
import React, { useRef, useState } from "react";
import { format } from "date-fns";
import { ADD_BULK_NOTIFICATION } from "../../../Apis/notification_api";
import { ToastShow } from "../../../Component/Toast/Toast";
import {
  downloadTemplate,
  readNotificationFile,
  sendToLabel,
} from "./notificationSheet";

const primaryButtonSx = {
  textTransform: "none",
  bgcolor: "#F8AF41",
  color: "white",
  "&:hover": { bgcolor: "rgb(173, 123, 44)" },
};

function ImportNotificationModal({ open, handleClose }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [fileError, setFileError] = useState("");
  const [isReading, setIsReading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const errorCount = rows.filter((r) => r.errors.length).length;
  const readyCount = rows.length - errorCount;
  const canImport = rows.length > 0 && errorCount === 0 && !isImporting;

  const onDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadTemplate();
    } catch (e) {
      console.log(e);
      ToastShow("Could not create the template. Please try again.");
    }
    setIsDownloading(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileName(file.name);
    setRows([]);
    setFileError("");
    setIsReading(true);
    try {
      const { rows, error } = await readNotificationFile(file);
      setRows(rows);
      setFileError(error);
    } catch (err) {
      console.log(err);
      setFileError("Could not read this file. Please use the template (.xlsx) or a .csv file.");
    }
    setIsReading(false);
  };

  const onImport = async () => {
    setIsImporting(true);
    const payload = rows.map((r) => ({
      app: r.app,
      date_time: r.date_time,
      title: r.title,
      message: r.message,
    }));
    const res = await ADD_BULK_NOTIFICATION(payload);
    setIsImporting(false);

    if (res && res.s == 1) {
      ToastShow(res.m || `${payload.length} notifications scheduled`);
      handleClose(1);
      return;
    }

    // Server rejected some rows (e.g. a time passed while previewing): show reasons, nothing was saved.
    const serverErrors = res?.r?.rows ?? [];
    if (serverErrors.length) {
      setRows((prev) =>
        prev.map((r) => {
          const se = serverErrors.find((x) => x.row === r.row);
          return se ? { ...r, errors: [se.reason] } : r;
        })
      );
    }
    ToastShow(res?.m ?? "Opps! something went wrong. Please try again.");
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isImporting && handleClose(0)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, color: "#454545" }}>
        Import notifications from Excel
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={
              isDownloading ? <CircularProgress size={16} /> : <Download />
            }
            onClick={onDownloadTemplate}
            disabled={isDownloading}
            sx={{ textTransform: "none" }}
          >
            Download template
          </Button>

          <Button
            startIcon={
              isReading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <UploadFile />
              )
            }
            onClick={() => inputRef.current?.click()}
            disabled={isReading || isImporting}
            sx={primaryButtonSx}
          >
            {fileName ? "Choose another file" : "Upload .xlsx / .csv"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            hidden
            onChange={onFile}
          />

          {fileName ? (
            <Typography variant="body2" color="text.secondary">
              {fileName}
            </Typography>
          ) : null}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Columns: <b>Date</b>, <b>Time</b> (24-hour, IST), <b>Title</b>,{" "}
          <b>Message</b>, <b>Send to</b> (All / Customer / Provider / Admin &
          Support). Date &amp; time must be in the future. Several
          notifications can share the same time.
        </Typography>

        {fileError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fileError}
          </Alert>
        ) : null}

        {rows.length ? (
          <>
            <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
              <Chip
                icon={<CheckCircle />}
                color="success"
                variant="outlined"
                label={`${readyCount} ready`}
              />
              {errorCount ? (
                <Chip
                  icon={<ErrorOutline />}
                  color="error"
                  variant="outlined"
                  label={`${errorCount} with errors`}
                />
              ) : null}
              {errorCount ? (
                <Typography variant="body2" color="error">
                  Fix these rows in the sheet and upload it again. Nothing is
                  saved until every row is valid.
                </Typography>
              ) : null}
            </Box>

            <TableContainer
              sx={{
                maxHeight: "50vh",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Date &amp; time</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Send to</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const hasError = r.errors.length > 0;
                    return (
                      <TableRow
                        key={r.row}
                        sx={hasError ? { bgcolor: "rgba(211, 47, 47, 0.06)" } : null}
                      >
                        <TableCell>{r.row}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.display ? format(r.display, "dd MMM yyyy, HH:mm") : "-"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" noWrap title={r.title}>
                            {r.title || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2" noWrap title={r.message}>
                            {r.message || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.app === null ? "-" : sendToLabel(r.app)}
                        </TableCell>
                        <TableCell>
                          {hasError ? (
                            <Tooltip title={r.errors.join(" · ")}>
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                              >
                                <ErrorOutline fontSize="small" />
                                {r.errors[0]}
                                {r.errors.length > 1 ? ` (+${r.errors.length - 1})` : ""}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="body2"
                              color="success.main"
                              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                            >
                              <CheckCircle fontSize="small" />
                              Ready
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => handleClose(0)}
          disabled={isImporting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={onImport}
          disabled={!canImport}
          startIcon={
            isImporting ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            ...primaryButtonSx,
            "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
          }}
        >
          {rows.length && !errorCount
            ? `Import ${rows.length} notification${rows.length === 1 ? "" : "s"}`
            : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImportNotificationModal;
